
import React, { useState, useMemo, useEffect } from 'react';
import { Transaction, CreditCard, CategoryType } from '../types';
import { CATEGORY_CONFIG } from '../constants';
import { formatCurrency, formatDate } from '../utils/helpers';
import TransactionModal from './TransactionModal';

interface TransactionsProps {
  transactions: Transaction[];
  onAddTransaction: (t: Transaction | Transaction[]) => void;
  onDeleteTransaction: (id: string) => void;
  onUpdateTransaction: (t: Transaction) => void;
  onTriggerVerify: (title: string, message: string, callback: () => void, icon?: string) => void;
  cards: CreditCard[];
  needsSetup: boolean;
  onTriggerSetup: () => void;
}

const Transactions: React.FC<TransactionsProps> = ({ 
  transactions = [], 
  onAddTransaction, 
  onDeleteTransaction, 
  onUpdateTransaction,
  onTriggerVerify,
  cards = [],
  needsSetup,
  onTriggerSetup
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [typeFilter, setTypeFilter] = useState<'all' | 'income' | 'expense'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [showValues, setShowValues] = useState(() => {
    const saved = localStorage.getItem('equilibra_privacy_mode');
    return saved === null ? true : saved === 'true';
  });

  useEffect(() => {
    const handleSync = () => {
      const saved = localStorage.getItem('equilibra_privacy_mode');
      setShowValues(saved === null ? true : saved === 'true');
    };
    window.addEventListener('privacyModeChanged', handleSync);
    return () => window.removeEventListener('privacyModeChanged', handleSync);
  }, []);

  const togglePrivacy = () => {
    const newVal = !showValues;
    setShowValues(newVal);
    localStorage.setItem('equilibra_privacy_mode', newVal.toString());
    window.dispatchEvent(new Event('privacyModeChanged'));
  };

  const maskValue = (val: number) => {
    if (!showValues) return "R$ ••••";
    return formatCurrency(val);
  };

  const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const yearsSet = new Set<number>([currentYear]);
    transactions.forEach(t => {
      if (t && t.date) {
        const y = parseInt(t.date.split('-')[0]);
        if (y) yearsSet.add(y);
      }
    });
    return Array.from(yearsSet).sort((a, b) => a - b);
  }, [transactions]);

  const filteredTransactions = useMemo(() => {
    if (!Array.isArray(transactions)) return [];
    
    return transactions.filter(t => {
      if (!t || !t.date || !t.description) return false;
      
      const d = new Date(t.date + 'T12:00:00');
      let m = d.getMonth();
      let y = d.getFullYear();

      if (t.cardId && t.cardId !== '') {
        const card = cards.find(c => c && c.id === t.cardId);
        const closing = card?.closingDate || 5;
        if (d.getDate() > closing) {
          m++;
          if (m > 11) { m = 0; y++; }
        }
      }

      const matchesType = typeFilter === 'all' || t.type === typeFilter;
      const matchesMonth = m === selectedMonth;
      const matchesYear = y === selectedYear;
      const matchesSearch = String(t.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesType && matchesMonth && matchesYear && matchesSearch;
    }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [transactions, typeFilter, selectedMonth, selectedYear, searchTerm, cards]);

  const handleAddClick = () => {
    if (needsSetup) {
      onTriggerSetup();
    } else {
      setEditingTransaction(null);
      setIsModalOpen(true);
    }
  };

  const handleEdit = (t: Transaction) => {
    onTriggerVerify(
      'Autorizar Edição',
      'Para modificar este lançamento, confirme sua identidade com a Chave Mestra.',
      () => {
        setEditingTransaction(t);
        setIsModalOpen(true);
      },
      'fa-pen-to-square'
    );
  };

  const handleDelete = (id: string) => {
    onDeleteTransaction(id);
  };

  const handleModalSubmit = (t: Transaction | Transaction[]) => {
    if (editingTransaction) {
      const updatedData = Array.isArray(t) ? t[0] : t;
      onUpdateTransaction(updatedData);
    } else {
      onAddTransaction(t);
    }
    setEditingTransaction(null);
    setIsModalOpen(false);
  };

  return (
    <div className="pb-24 max-w-5xl mx-auto">
      <div className="sticky top-0 z-40 bg-slate-100/80 dark:bg-slate-950/80 backdrop-blur-xl pt-[max(1rem,env(safe-area-inset-top))] pb-6 space-y-4 -mx-4 px-4 border-b border-slate-200/50 dark:border-slate-800/50 md:relative md:bg-transparent md:border-none md:p-0 md:mb-8 transition-all">
        <header className="flex justify-between items-center">
          <div>
            <h1 className="text-xl md:text-3xl font-black text-slate-900 dark:text-slate-50 heading-display uppercase tracking-tight">Lançamentos</h1>
            <div className="flex items-center gap-2 mt-1">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse shadow-[0_0_8px_rgba(59,130,246,0.5)]"></div>
              <p className="text-slate-500 dark:text-slate-400 text-[9px] md:text-xs uppercase font-black tracking-[0.15em]">Histórico Detalhado • Soberania de Dados</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={togglePrivacy}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-400 dark:text-slate-500 w-9 h-9 md:w-12 md:h-12 rounded-xl flex items-center justify-center shadow-premium active:scale-95 transition-all hover:border-blue-500/50 group"
              title={showValues ? "Ocultar Valores" : "Mostrar Valores"}
            >
              <i className={`fa-solid ${showValues ? 'fa-eye' : 'fa-eye-slash'} text-xs md:text-lg transition-transform group-hover:scale-110`}></i>
            </button>
            <button onClick={handleAddClick} className="bg-blue-600 hover:bg-blue-700 text-white w-9 h-9 md:w-12 md:h-12 rounded-xl flex items-center justify-center shadow-xl shadow-blue-500/20 active:scale-95 transition-all group">
              <i className="fa-solid fa-plus text-sm md:text-xl transition-transform group-hover:rotate-90"></i>
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-7 flex gap-2">
            <div className="relative flex-1 group">
              <select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))} className="w-full bg-white dark:bg-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-800 rounded-xl text-[9px] font-black uppercase tracking-widest p-3 outline-none px-4 cursor-pointer appearance-none hover:border-blue-500/30 transition-all shadow-sm group-hover:shadow-md">
                {months.map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
              <i className="fa-solid fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-[9px] group-hover:text-blue-500 transition-colors"></i>
            </div>
            <div className="relative w-24 md:w-28 group">
              <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} className="w-full bg-white dark:bg-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-800 rounded-xl text-[9px] font-black uppercase tracking-widest p-3 outline-none px-4 cursor-pointer appearance-none hover:border-blue-500/30 transition-all shadow-sm group-hover:shadow-md">
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <i className="fa-solid fa-calendar absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-[9px] group-hover:text-blue-500 transition-colors"></i>
            </div>
          </div>
          <div className="md:col-span-5 relative group">
            <i className="fa-solid fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xs group-focus-within:text-blue-500 transition-colors"></i>
            <input 
              type="text"
              placeholder="PESQUISAR LANÇAMENTO..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white dark:bg-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-800 rounded-xl pl-10 pr-4 py-3 text-[9px] font-black uppercase tracking-widest outline-none shadow-premium placeholder:text-slate-400 focus:border-blue-500/50 transition-all focus:shadow-blue-500/5"
            />
          </div>
        </div>

        <div className="flex bg-slate-100 dark:bg-slate-900/50 p-1 rounded-xl border border-slate-200/50 dark:border-slate-800/50 shadow-inner">
          {['all', 'income', 'expense'].map((f) => (
            <button
              key={f}
              onClick={() => setTypeFilter(f as any)}
              className={`flex-1 py-2 rounded-lg text-[9px] font-black uppercase tracking-[0.15em] transition-all duration-300 ${typeFilter === f ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-premium scale-[1.02]' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'}`}
            >
              {f === 'all' ? 'Ver Tudo' : f === 'income' ? 'Entradas' : 'Saídas'}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {filteredTransactions.length === 0 ? (
          <div className="py-40 text-center animate-in fade-in duration-1000">
            <div className="w-24 h-24 bg-slate-100 dark:bg-slate-900 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8 text-slate-300 dark:text-slate-800 shadow-inner">
              <i className="fa-solid fa-receipt text-4xl"></i>
            </div>
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Nada encontrado neste período</p>
          </div>
        ) : (
          filteredTransactions.map((t, idx) => {
            const card = t.cardId && Array.isArray(cards) ? cards.find(c => c && c.id === t.cardId) : null;
            const isOldCard = t.cardId && !card;
            const paymentLabel = card ? card.name : (isOldCard ? 'Cartão (Excluído)' : 'Dinheiro / PIX');
            const paymentIcon = (card || isOldCard) ? 'fa-credit-card' : 'fa-wallet';
            const categoryInfo = CATEGORY_CONFIG[t.category as CategoryType] || CATEGORY_CONFIG['Outros'];

            return (
              <div 
                key={t.id} 
                className="bg-white dark:bg-slate-900 p-4 md:p-5 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-premium flex items-center gap-4 md:gap-6 transition-all hover:shadow-xl hover:translate-x-1 group animate-in slide-in-from-left duration-500 relative overflow-hidden"
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                <div className={`absolute top-0 left-0 w-1 h-full ${t.type === 'income' ? 'bg-emerald-500' : 'bg-red-500'} opacity-0 group-hover:opacity-100 transition-opacity`}></div>
                
                <div className={`${categoryInfo.color} w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center text-white shrink-0 shadow-xl shadow-current/20 text-lg md:text-xl transition-all group-hover:scale-110 group-hover:rotate-3 duration-500 relative`}>
                  <div className="absolute inset-0 bg-white/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  {categoryInfo.icon}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
                    <h4 className="font-black text-slate-900 dark:text-slate-100 text-sm md:text-base leading-tight break-words heading-display uppercase tracking-tight group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {t.description}
                    </h4>
                    <span className={`px-2 py-0.5 rounded-full text-[7px] font-black uppercase tracking-widest flex items-center gap-1.5 border shrink-0 transition-all ${ (card || isOldCard) ? 'bg-blue-500/10 text-blue-600 border-blue-500/20 group-hover:bg-blue-500 group-hover:text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 group-hover:bg-slate-200 dark:group-hover:bg-slate-700'}`}>
                      <i className={`fa-solid ${paymentIcon}`}></i>
                      {paymentLabel}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400 dark:text-slate-500 text-[9px] font-black uppercase tracking-widest flex items-center gap-1">
                      <div className={`w-1 h-1 rounded-full ${categoryInfo.color}`}></div>
                      {t.category}
                    </span>
                    <div className="w-0.5 h-0.5 rounded-full bg-slate-200 dark:bg-slate-800"></div>
                    <span className="text-slate-400 dark:text-slate-500 text-[9px] font-bold uppercase tracking-widest">{formatDate(t.date)}</span>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-1 md:gap-2 shrink-0">
                  <p className={`font-black text-base md:text-xl heading-display tracking-tighter transition-transform group-hover:scale-105 ${t.type === 'income' ? 'text-emerald-600' : 'text-red-600'}`}>
                    {t.type === 'income' ? '+ ' : '- '} {maskValue(t.amount)}
                  </p>
                  <div className="flex gap-1.5 md:gap-2 opacity-0 group-hover:opacity-100 transition-all transform translate-y-1 group-hover:translate-y-0 duration-300">
                    <button 
                      onClick={() => handleEdit(t)}
                      className="w-8 h-8 md:w-9 md:h-9 bg-slate-50 dark:bg-slate-800 text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg flex items-center justify-center transition-all shadow-sm border border-slate-100 dark:border-slate-800 active:scale-90"
                      title="Editar"
                    >
                      <i className="fa-solid fa-pen text-[10px]"></i>
                    </button>
                    <button 
                      onClick={() => handleDelete(t.id)}
                      className="w-8 h-8 md:w-9 md:h-9 bg-slate-50 dark:bg-slate-800 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg flex items-center justify-center transition-all shadow-sm border border-slate-100 dark:border-slate-800 active:scale-90"
                      title="Excluir"
                    >
                      <i className="fa-solid fa-trash text-[10px]"></i>
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <TransactionModal 
        isOpen={isModalOpen} 
        onClose={() => { setIsModalOpen(false); setEditingTransaction(null); }} 
        onAdd={handleModalSubmit}
        cards={cards}
        transactions={transactions}
        initialData={editingTransaction || undefined}
      />
    </div>
  );
};

export default Transactions;
